import {
    type CountryCode,
    isSupportedCountry,
    type PhoneNumber,
    parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { z } from 'zod';

// Phone numbers are stored as people read them, such as (416) 555-0100. Most were typed into free
// text fields, so these read a number without a dialing code as Canadian unless told otherwise.
// See docs/phone-numbers.md

const DEFAULT_COUNTRY: CountryCode = 'CA';

/** A phone's parts, as Treefort and the ID verification modals take them */
export interface PhoneNumberParts {
    /** Digits without the dialing code, such as '4165550100' */
    nationalNumber: string;
    /** Lowercase ISO country code, such as 'ca' */
    countryCode: string;
    dialCode: number;
}

/** defaultCountry is the ISO code a number without a dialing code is read in, in either case */
function parsePhoneNumber(
    value: string | null | undefined,
    defaultCountry?: string | null,
): PhoneNumber | undefined {
    if (!value?.trim()) return undefined;
    const country = defaultCountry?.toUpperCase();
    return parsePhoneNumberFromString(
        value,
        country && isSupportedCountry(country) ? country : DEFAULT_COUNTRY,
    );
}

/** The number in E.164, or null when it isn't a valid phone number */
export function toE164PhoneNumber(
    value: string | null | undefined,
    defaultCountry?: string | null,
): string | null {
    const phone = parsePhoneNumber(value, defaultCountry);
    return phone?.isValid() ? phone.number : null;
}

/**
 * A valid phone number, as the API takes it, stored as people read it (formatPhoneNumber). One
 * without a dialing code is read as Canadian
 */
export const PhoneNumberSchema = z
    .string()
    .refine((value) => toE164PhoneNumber(value) !== null, {
        message: 'Must be a valid phone number, such as (416) 555-0100',
    })
    .transform(formatPhoneNumber);

/** The parts of a valid phone number, or null when it isn't one */
export function getPhoneNumberParts(value: string | null | undefined): PhoneNumberParts | null {
    const phone = parsePhoneNumber(value);
    if (!phone?.isValid() || !phone.country) return null;
    return {
        nationalNumber: phone.nationalNumber,
        countryCode: phone.country.toLowerCase(),
        dialCode: Number(phone.countryCallingCode),
    };
}

/**
 * A phone as people read it: '(416) 555-0100' for North American numbers, '+44 7400 123456' for
 * others. One that isn't a valid number is shown as it was typed
 */
export function formatPhoneNumber(value: string): string {
    const phone = parsePhoneNumber(value);
    if (!phone?.isValid()) return value.trim();
    return phone.countryCallingCode === '1' ? phone.formatNational() : phone.formatInternational();
}
